import React, { useState, useEffect } from 'react';
import {
  Box,
  Input,
  Typography,
  Autocomplete,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Button,
  FormControl,
  Popover,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ColoredFilterIcon from '../../../../assets/Admin/RequestImages/colored-filter-icon.svg';
import FilterIcon from '../../../../assets/Admin/RequestImages/filter-icon.svg';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import './Transactions.css';
import { PaymentTable } from '../../../../components/Admin/Payments/Transactions/PaymentTable';
// import { userRoleArray, StatusArray } from '../../../../mock/PaymentData';
import { axiosInstance } from '../../../../utils/tokenUtils';

const StatusArray = [
  { status: 'All' },
  { status: 'Completed' },
  { status: 'Failed' },
  // { status: 'Pending' },
];

const userRoleArray = [{ userRole: 'All' }, { userRole: 'Learner' }, { userRole: 'Instructor' }];

const Transactions = () => {
  const [searchTerm, setSearchTerm] = useState(''); // UI input value
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(''); // Applied search
  const [paymentData, setPaymentData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  // Applied filter states (used for filtering)
  const [userRole, setUserRole] = useState('All');
  const [status, setStatus] = useState('All');
  const [country, setCountry] = useState('All');
  // UI (pending) filter states inside modal
  const [uiUserRole, setUiUserRole] = useState('All');
  const [uiStatus, setUiStatus] = useState('All');
  const [uiCountry, setUiCountry] = useState('All');
  const [countries, setCountries] = useState(['All']);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const handleChangeSearch = e => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = e => {
    if (e.key === 'Enter') {
      setAppliedSearchTerm(searchTerm.trim());
      setCurrentPage(1);
    }
  };

  const handleClickToOpenFilter = event => {
    // Initialize UI states with current applied states when opening
    setUiUserRole(userRole);
    setUiStatus(status);
    setUiCountry(country);
    setFilterAnchorEl(event.currentTarget);
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
    setOpenFilter(false);
  };

  const handleApplyFilter = () => {
    // Apply UI values to applied filter values
    setUserRole(uiUserRole);
    setStatus(uiStatus);
    setCountry(uiCountry);
    setCurrentPage(1);
    handleCloseFilter();
  };

  const handleClearAll = () => {
    setUiUserRole('All');
    setUiStatus('All');
    setUiCountry('All');
  };

  // Pagination handlers
  const handlePageChange = newPage => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = event => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const toApiUserType = value => {
    if (!value || value === 'All') return undefined;
    const lower = String(value).toLowerCase();
    if (lower === 'learner') return 'student';
    if (lower === 'instructor') return 'teacher';
    return lower;
  };

  const toApiStatus = value => {
    if (!value || value === 'All') return undefined;
    const lower = String(value).toLowerCase();
    if (lower === 'completed') return 'success';
    if (lower === 'failed') return 'failed';
    // if (lower === 'pending') return 'pending';
    return lower;
  };

  // Fetch transactions with pagination and server-side filters
  const fetchTransactions = async (page = currentPage, size = pageSize, overrides = {}) => {
    setIsLoading(true);
    try {
      const apiUserType = toApiUserType(overrides.userRole ?? userRole);
      const apiStatus = toApiStatus(overrides.status ?? status);
      const apiCountry = overrides.country ?? country;
      const apiSearch = overrides.search ?? appliedSearchTerm;

      const queryParams = new URLSearchParams({
        page: String(page),
        page_size: String(size),
      });

      if (apiUserType) queryParams.append('user_type', apiUserType);
      if (apiStatus) queryParams.append('status', apiStatus);
      if (apiCountry && apiCountry !== 'All')
        queryParams.append('country', String(apiCountry).toLowerCase());
      if (apiSearch) queryParams.append('search_query', apiSearch);

      const url = `/admin/transactions?${queryParams.toString()}`;
      const response = await axiosInstance.get(url);
      const records = response.data?.results || [];
      const count = response.data?.count || 0;

      // Helper mappers
      const mapStatus = raw => {
        if (!raw) return raw;
        const lower = raw.toLowerCase();
        if (lower === 'success' || lower === 'completed') return 'Completed';
        if (lower === 'failed') return 'Failed';
        // if (lower === 'pending') return 'Pending';
        return raw;
      };

      const mapped = records.map(rec => ({
        paymentId: rec.id,
        apsPaymentId: rec.aps_payment_fort_id,
        name: `${rec.first_name ?? ''} ${rec.last_name ?? ''}`.trim(),
        userRole:
          rec.user_role?.toLowerCase() === 'student' || rec.user_role?.toLowerCase() === 'learner'
            ? 'Learner'
            : rec.user_role?.toLowerCase() === 'teacher' ||
                rec.user_role?.toLowerCase() === 'instructor'
              ? 'Instructor'
              : rec.user_role,
        paymentFor:
          rec.payment_type?.toLowerCase() === 'wallet top up' ? 'Wallet Top Up' : 'Application Fee',
        userImage: '/placeholder.svg',
        dateTime: rec.date_time,
        amount: rec.amount,
        country: rec.country,
        status: mapStatus(rec.status),
      }));

      setPaymentData(mapped);
      setTotalCount(count);
      setTotalPages(Math.ceil(count / size));
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setPaymentData([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch transactions on mount and whenever pagination or applied filters/search change
  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, userRole, status, country, appliedSearchTerm]);

  // Fetch countries for filter
  useEffect(() => {
    let isMounted = true;
    const fetchCountries = async () => {
      try {
        const res = await axiosInstance.get('/auth/countries');
        const names = (res?.data || []).map(item => item?.name).filter(Boolean);
        if (isMounted) {
          setCountries(['All', ...names]);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
        if (isMounted) {
          setCountries(['All']);
        }
      }
    };
    fetchCountries();
    return () => {
      isMounted = false;
    };
  }, []);

  // Generate pagination items
  const generatePaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        items.push(i);
      }
    } else {
      // Show first page, last page, and pages around current
      items.push(1);

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      if (start > 2) {
        items.push('...');
      }

      for (let i = start; i <= end; i++) {
        items.push(i);
      }

      if (end < totalPages - 1) {
        items.push('...');
      }

      if (totalPages > 1) {
        items.push(totalPages);
      }
    }

    return items;
  };

  return (
    <Layout title="Klass Online | PayIn">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-4">
            <Box className="flex justify-between items-center">
              <div className="dispute-title"> PayIn </div>
              <Box className="flex items-center gap-4 w-[40%]">
                <Input
                  sx={{
                    textDecoration: 'none',
                    border: '1px solid var(--Stroke, #D5DEE3)',
                    borderRadius: '56px',
                    '&:before, &:after': {
                      borderBottom: 'none !important',
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: 'none !important',
                    },
                    padding: '8px 10px',
                    '& .MuiInputBase-input': {
                      padding: '3px 5px 3px 5px',
                    },
                    width: '100%',
                    height: '40px',
                  }}
                  type="search"
                  placeholder="Search"
                  aria-label="Search"
                  value={searchTerm}
                  onChange={handleChangeSearch}
                  onKeyDown={handleSearchKeyDown}
                  startAdornment={<SearchIcon className="search-icon-css" />}
                />
                {openFilter ? (
                  <img
                    src={ColoredFilterIcon || '/placeholder.svg'}
                    alt="filter"
                    onClick={handleCloseFilter}
                    style={{ width: 25, height: 25, cursor: 'pointer' }}
                  />
                ) : (
                  <img
                    src={FilterIcon || '/placeholder.svg'}
                    alt="filter"
                    onClick={handleClickToOpenFilter}
                    style={{ width: 25, height: 25, cursor: 'pointer' }}
                  />
                )}
              </Box>
            </Box>
            <Popover
              open={openFilter}
              anchorEl={filterAnchorEl}
              onClose={handleCloseFilter}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ className: 'transactions-filter-modal-container' }}
            >
              <Box className="transactions-filter-modal-header">
                <Typography className="transactions-filter-modal-title">Filters</Typography>
                <Typography
                  className="transactions-filter-clear-all-button"
                  onClick={handleClearAll}
                >
                  Clear All
                </Typography>
              </Box>
              <Box className="transactions-filter-modal-content">
                <Box className="transactions-filter-grid">
                  {/* User Role */}
                  <Box className="transactions-filter-item">
                    <label className="transactions-filter-label">User Role</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={uiUserRole}
                        onChange={e => setUiUserRole(e.target.value)}
                        displayEmpty
                        className="transactions-filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }}
                      >
                        {userRoleArray.map(option => (
                          <MenuItem
                            key={option.userRole}
                            value={option.userRole}
                            className="transactions-dropdown-option"
                          >
                            {option.userRole}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Status */}
                  <Box className="transactions-filter-item">
                    <label className="transactions-filter-label">Status</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={uiStatus}
                        onChange={e => setUiStatus(e.target.value)}
                        displayEmpty
                        className="transactions-filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }}
                      >
                        {StatusArray.map(option => (
                          <MenuItem
                            key={option.status}
                            value={option.status}
                            className="transactions-dropdown-option"
                          >
                            {option.status}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Country */}
                  <Box className="transactions-filter-item">
                    <label className="transactions-filter-label">Country</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={uiCountry}
                        onChange={e => setUiCountry(e.target.value)}
                        displayEmpty
                        className="transactions-filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }}
                      >
                        <MenuItem value="All" className="transactions-dropdown-option">
                          All
                        </MenuItem>
                        {countries
                          .filter(c => c && c !== 'All')
                          .map(name => (
                            <MenuItem
                              key={name}
                              value={name}
                              className="transactions-dropdown-option"
                            >
                              {name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>
              <Box className="transactions-filter-buttons-container">
                <Button
                  variant="outlined"
                  onClick={handleClearAll}
                  className="transactions-filter-clear-button"
                >
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApplyFilter}
                  className="transactions-filter-apply-button"
                >
                  Apply
                </Button>
              </Box>
            </Popover>
            <PaymentTable data={paymentData} isLoading={isLoading} />

            <Box className="transactions-pagination-container">
              <Box className="transactions-pagination-box">
                <Typography className="transactions-pagination-text">Showing</Typography>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="transactions-pagination-select"
                  sx={{
                    height: '30px',
                    minWidth: '60px',
                    margin: '0 4px',
                    '& .MuiSelect-select': {
                      padding: '6px 24px 6px 8px',
                      textAlign: 'center',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& .MuiSelect-icon': {
                      right: '4px',
                      color: '#666',
                    },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={15}>15</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                </Select>
                <Typography className="transactions-pagination-text">
                  of {totalCount} transactions
                </Typography>
              </Box>

              <nav>
                <ul className="transactions-pagination-list">
                  {/* Previous button */}
                  <li>
                    <button
                      type="button"
                      className={`transactions-pagination-btn ${currentPage === 1 ? 'transactions-pagination-btn-disabled' : ''}`}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>

                  {/* Page numbers */}
                  {generatePaginationItems().map((item, index) => (
                    <li key={index}>
                      {item === '...' ? (
                        <span className="transactions-pagination-ellipsis">…</span>
                      ) : (
                        <button
                          type="button"
                          className={`transactions-pagination-btn ${currentPage === item ? 'transactions-pagination-btn-selected' : ''}`}
                          onClick={() => handlePageChange(item)}
                        >
                          {item}
                        </button>
                      )}
                    </li>
                  ))}

                  {/* Next button */}
                  <li>
                    <button
                      type="button"
                      className={`transactions-pagination-btn ${currentPage === totalPages ? 'transactions-pagination-btn-disabled' : ''}`}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default Transactions;
